import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkWithHref } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import type {
  DrawerInterface,
  DrawerOptions,
  DropdownInterface,
  DropdownOptions,
  InstanceOptions,
  TooltipInterface,
  TooltipOptions,
} from 'flowbite';
import { Drawer, Dropdown, Tooltip } from 'flowbite';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { PageService } from 'src/app/core/page/page.service';
import { selectColorTheme } from 'src/app/store/color-theme/selectors';
import { selectActiveLanguage } from 'src/app/store/language/selectors';
import { AppStateInterface } from 'src/app/store/types';
import { NgClass, NgIf, AsyncPipe } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapMoonFill, bootstrapSunFill } from '@ng-icons/bootstrap-icons';
import { heroBars3, heroXMark } from '@ng-icons/heroicons/outline';
import { BrowserService } from 'src/app/core/browser.service';
import { log } from 'console';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [
        RouterLink,
        RouterLinkWithHref,
        NgClass,
        NgIf,
        NgIcon,
        RouterLink,
        AsyncPipe,
        TranslateModule,
    ],
    providers: [
      provideIcons({
        bootstrapSunFill,
        bootstrapMoonFill,
        heroBars3,
        heroXMark
      })
    ]
})
export class HeaderComponent implements AfterViewInit {
  private readonly defaultLanguage = 'en';

  currentLocation: BehaviorSubject<string | null> = new BehaviorSubject<
  string | null
>(null);
  currentActiveLanuage: string = this.defaultLanguage;
  activeLanguage: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);
  langaugeToggler: boolean = false;
  showDrawer: boolean = false;
  tooltipLanguageButtonDisabledReason =
    'Diese Seite ist nur auf Deustch verfügbar';

  @ViewChild('languageDropdownButton')
  languageDropdownButton!: ElementRef;
  @ViewChild('languageDropdownMenu', { static: false })
  languageDropdownMenu!: ElementRef;
  @ViewChild('languageDropdownMobileButton', { static: false })
  languageDropdownMobileButton!: ElementRef;
  @ViewChild('languageDropdownMobileMenu', { static: false })
  languageDropdownMobileMenu!: ElementRef;

  @ViewChild('tooltipContent', { static: false })
  tooltipContent!: ElementRef;

  @ViewChild('navbarDrawerOpenButton', { static: false })
  navbarDrawerOpenButton!: ElementRef;
  @ViewChild('navbarDrawerCloseButton', { static: false })
  navbarDrawerCloseButton!: ElementRef;
  @ViewChild('navbarDrawerMenu', { static: false })
  navbarDrawerMenu!: ElementRef;

  drawer!: DrawerInterface;

  disableLanguageDropdown: boolean = false;
  selectedColortheme!: string;
  selectedColortheme$!: Observable<string>;


  constructor(
    public readonly _pageService: PageService,
    private _router: Router,
    private _translate: TranslateService,
    private _store: Store<AppStateInterface>,
    private _browserService: BrowserService,
    public activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    const urlFragments = this._router.url.split('/');
    if(urlFragments && urlFragments.length > 1){
      const activeLanguage = urlFragments[1];
      this._translate.setDefaultLang(activeLanguage);
      this._translate.use(activeLanguage);
      this._store.select(selectActiveLanguage).subscribe({
        next: currentLanguage => {
          this.activeLanguage.next(currentLanguage);
        },
        error: error => {
          //ToDo
        }
      })
    }

    this.selectedColortheme$ = this._store.select(selectColorTheme)
    this.hilightRouterPosition()
    var urlChunks = this._router.url.split('/');
    this.disableLanguageDropdown = urlChunks.includes('employers');
  }

  ngAfterViewInit() {
    if(this._browserService.isBrowserPlatform()){
      this.addEventListenerToDropdown(
        this.languageDropdownButton.nativeElement,
        this.languageDropdownMenu.nativeElement,
      );
      this.addEventListenerToDropdown(
        this.languageDropdownMobileButton.nativeElement,
        this.languageDropdownMobileMenu.nativeElement,
      );

      this.initDrawerMenu(this.navbarDrawerMenu.nativeElement);

      this.initlanguageButtonToolTip(
        this.tooltipContent,
        this.languageDropdownButton,
      );
    }
  }

  initDrawerMenu(drawerMenuElement: HTMLElement){
    const options: DrawerOptions = {
      placement: 'right',
      backdrop: true,
      bodyScrolling: false,
      edge: false,
      edgeOffset: '',
      backdropClasses:
          'bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-30',
       onShow: (drawer: DrawerInterface) => {
        this.navbarDrawerMenu.nativeElement.style.transform = '';
      },
      onHide: (drawer: DrawerInterface) => {
        this.navbarDrawerMenu.nativeElement.style.transform = 'translateX(200%)';

      },
      onToggle: () => {
          console.log('drawer has been toggled');
      },
    };

    // instance options object
    const instanceOptions: InstanceOptions = {
      id: 'page-drawer-Menu',
      override: true
    };
    this.drawer = new Drawer(drawerMenuElement, options, instanceOptions);
  }

  setLanguage(language: string): void {
    this._pageService.setLanguage(language);
    this._store.select(selectActiveLanguage).subscribe({
      next: (activeLanguage) => {
        this.activeLanguage.next(activeLanguage);
        this.closeDrawerMenu();
      },
    });
    this._translate.use(language);
  }

  private addEventListenerToDropdown(
    nativeElementButton: HTMLElement,
    nativeElementMenu: HTMLElement,
  ): void {
    const options: DropdownOptions = {
      placement: 'bottom',
      triggerType: 'click',
      offsetSkidding: 0,
      offsetDistance: 5,
      delay: 0,
    };

    const dropdown: DropdownInterface = new Dropdown(
      nativeElementMenu,
      nativeElementButton,
      options,
    );

    nativeElementMenu.addEventListener('click', (e: any) => {
      dropdown.hide();
    });
  }

  openDrawerMenu(){
    this.drawer.show()
  }

  closeDrawerMenu(){
    this.drawer.hide()
  }


  private initlanguageButtonToolTip(
    tooltipContent: ElementRef,
    tooltipElement: ElementRef,
  ) {
    const options: TooltipOptions = {
      placement: 'top',
      triggerType: 'hover',
      onHide: () => {},
      onShow: () => {
        if (this.currentLocation.getValue() != 'employers') {
          tooltip.hide();
        }
      },
    };
    const tooltip: TooltipInterface = new Tooltip(
      tooltipContent.nativeElement,
      tooltipElement.nativeElement,
      options,
    );
  }

  /**
   * Method hilights the header depending on the selected route
   *
   * initially no routing event is executed therefore we need to set the current location when the method is executed
   */
  private hilightRouterPosition(){
    const location = this._router.url.split('/').pop();
    if(location && ['en', 'fa', 'de'].includes(location)) {
      this.currentLocation.next('');
    } else if(location) {
      this.currentLocation.next(location);
    }
    this._router.events
    .pipe(filter((event) => event instanceof NavigationEnd))
    .subscribe(() => {
      const location = this._router.url.split('/').pop();
      if(location && ['en', 'fa', 'de'].includes(location)) {
        this.currentLocation.next('');
      } else if(location) {
        this.currentLocation.next(location);
      }
      console.log(this.currentLocation.getValue())
    });
  }
}
